import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useLoginMutation } from "../services/api";
import { setCredentials } from "../features/auth/authSlice";
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import logoImage from "../assets/logo.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const result = await login({ email, password }).unwrap();
      dispatch(setCredentials(result.data));
      navigate("/dashboard");
    } catch (err) {
      setError(err?.data?.message || "Login failed. Please check your credentials.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      background: "#F8F9FA"
    }}>
      {/* Left Side - Branding */}
      <div style={{
        flex: "0 0 50%",
        background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
        padding: "4rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        overflow: "hidden"
      }}>
        {/* Animated Background Pattern */}
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `radial-gradient(circle at 20% 50%, #FFD700 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, #FFD700 0%, transparent 50%),
                           radial-gradient(circle at 40% 90%, #FFD700 0%, transparent 50%)`,
          animation: "float 20s ease-in-out infinite"
        }}></div>

        {/* Content */}
        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Logo */}
          <div style={{
            width: "80px",
            height: "80px",
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "3rem",
            boxShadow: "0 8px 24px rgba(255, 215, 0, 0.3)"
          }}>
            <img 
              src={logoImage} 
              alt="Roha Jewellery" 
              style={{
                width: "50px",
                height: "50px",
                objectFit: "contain"
              }}
            />
          </div>

          {/* Main Text */}
          <h1 style={{
            fontSize: "3rem",
            fontWeight: 700,
            color: "white",
            marginBottom: "1.5rem",
            lineHeight: 1.2
          }}>
            Welcome to<br />Roha Jewellery
          </h1>
          
          <p style={{
            fontSize: "1.125rem",
            color: "rgba(255, 255, 255, 0.7)",
            lineHeight: 1.6,
            maxWidth: "480px",
            marginBottom: "3rem"
          }}>
            Your complete jewellery management solution. Manage inventory, sales, customers, and more all in one place.
          </p>

          {/* Features */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            {[
              { icon: "📊", text: "Real-time Analytics & Reports" },
              { icon: "💎", text: "Inventory & Stock Management" },
              { icon: "🛒", text: "Point of Sale System" },
              { icon: "👥", text: "Customer Relationship Management" }
            ].map((feature, index) => (
              <div key={index} style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                color: "white",
                fontSize: "0.95rem"
              }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  background: "rgba(255, 215, 0, 0.1)",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.25rem"
                }}>
                  {feature.icon}
                </div>
                <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          position: "relative",
          zIndex: 1,
          color: "rgba(255, 255, 255, 0.5)",
          fontSize: "0.875rem"
        }}>
          © 2026 Roha Jewellery. All rights reserved.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{
        flex: "0 0 50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4rem 2rem"
      }}>
        <div style={{
          width: "100%",
          maxWidth: "460px"
        }}>
          {/* Header */}
          <div style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontSize: "2rem",
              fontWeight: 700,
              color: "#1E293B",
              marginBottom: "0.5rem"
            }}>
              Sign in to your account
            </h2>
            <p style={{
              fontSize: "0.95rem",
              color: "#64748B"
            }}>
              Enter your credentials to access the dashboard
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div style={{
              padding: "1rem",
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: "12px",
              marginBottom: "2rem",
              display: "flex",
              alignItems: "flex-start",
              gap: "0.75rem",
              animation: "slideDown 0.3s ease-out"
            }}>
              <FiAlertCircle size={20} style={{ color: "#EF4444", flexShrink: 0, marginTop: "2px" }} />
              <span style={{ fontSize: "0.875rem", color: "#991B1B" }}>{error}</span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            {/* Email Field */}
            <div>
              <label style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#1E293B",
                marginBottom: "0.5rem"
              }}>
                Email Address
              </label>
              <div style={{ position: "relative" }}>
                <div style={{
                  position: "absolute",
                  left: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94A3B8",
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none"
                }}>
                  <FiMail size={20} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={{
                    width: "100%",
                    height: "52px",
                    padding: "0 1rem 0 3rem",
                    fontSize: "0.95rem",
                    border: "2px solid #E2E8F0",
                    borderRadius: "12px",
                    background: "white",
                    color: "#1E293B",
                    transition: "all 0.2s",
                    outline: "none"
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#3B82F6";
                    e.currentTarget.style.boxShadow = "0 0 0 4px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "#E2E8F0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label style={{
                display: "block",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#1E293B",
                marginBottom: "0.5rem"
              }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <div style={{
                  position: "absolute",
                  left: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94A3B8",
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none"
                }}>
                  <FiLock size={20} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: "100%",
                    height: "52px",
                    padding: "0 3rem 0 3rem",
                    fontSize: "0.95rem",
                    border: "2px solid #E2E8F0",
                    borderRadius: "12px",
                    background: "white",
                    color: "#1E293B",
                    transition: "all 0.2s",
                    outline: "none"
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#3B82F6";
                    e.currentTarget.style.boxShadow = "0 0 0 4px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "#E2E8F0";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "#64748B",
                    cursor: "pointer",
                    padding: "0.25rem",
                    display: "flex",
                    alignItems: "center",
                    transition: "color 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "#1E293B"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "#64748B"}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>

            {/* Sign In Button */}
            <button 
              type="submit" 
              disabled={isLoading}
              style={{
                width: "100%",
                height: "52px",
                marginTop: "0.5rem",
                background: isLoading ? "#94A3B8" : "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                color: "white",
                border: "none",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                boxShadow: isLoading ? "none" : "0 4px 12px rgba(59, 130, 246, 0.3)",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = isLoading ? "none" : "0 4px 12px rgba(59, 130, 246, 0.3)";
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: "20px",
                    height: "20px",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderTopColor: "white",
                    borderRadius: "50%",
                    animation: "spin 0.6s linear infinite"
                  }}></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <FiCheckCircle size={20} />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Help Text */}
          <div style={{
            marginTop: "2rem",
            padding: "1.25rem",
            background: "#F8FAFC",
            borderRadius: "12px",
            border: "1px solid #E2E8F0"
          }}>
            <p style={{
              fontSize: "0.875rem",
              color: "#64748B",
              margin: 0,
              lineHeight: 1.6
            }}>
              💡 <strong style={{ color: "#1E293B" }}>Having trouble signing in?</strong><br />
              Contact your system administrator for assistance.
            </p>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 1024px) {
          /* Hide left side on tablets */
          div[style*="flex: 0 0 50%"]:first-child {
            display: none !important;
          }
          div[style*="flex: 0 0 50%"]:last-child {
            flex: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}
